package com.example.mystybag.login.logic

import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.logic.base.ParentCallback
import com.example.mystybag.core.model.CountryItem
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.common.resources.SR
import com.example.mystybag.login.model.AddMolRequiredField
import com.example.mystybag.login.model.AddMolUIState
import com.example.mystybag.login.model.SignatoryItem
import kotlinx.coroutines.launch

class AddMolViewModel(
    context: AppContext,
    private val config: NavigatorAddMol
) : ChildViewModel<AddMolUIState>(context, AddMolUIState()) {

    private val sharedConfig: MerchantRegistrationSharedConfig
        get() = config.sharedConfig

    init {
        val editingIndex = config.editingIndex
        val signatories = sharedConfig.signatories
        if (editingIndex != null && editingIndex in signatories.indices) {
            val signatory = signatories[editingIndex]
            updateState {
                it.copy(
                    firstName = signatory.firstName,
                    lastName = signatory.lastName,
                    phoneNumber = signatory.phoneNumber.ifBlank { sharedConfig.companyPhoneNumber },
                    dialCode = signatory.dialCode.ifBlank { sharedConfig.companyDialCode }
                )
            }
        } else {
            updateState {
                it.copy(dialCode = sharedConfig.companyDialCode)
            }
        }
    }

    override fun onCreate() {
        navControls.backButtonVisible = true
        navControls.title = sharedString(SR.strings.registration)
        loadCountries()
    }

    private fun loadCountries() {
        scope.launch {
            commandBlock(showProgress = false) {
                CommonCommands.sendGetCountries()
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                updateState {
                                    it.copy(
                                        countries = response.countriesList?.map { country ->
                                            CountryItem(
                                                id = country.id,
                                                name = country.name,
                                                dialCode = country.dialCode,
                                                isoAlpha2 = country.isoAlpha2
                                            )
                                        } ?: emptyList()
                                    )
                                }
                            }
                        }
                    )
            }
        }
    }

    fun updateFirstName(name: String) {
        updateState {
            it.copy(
                firstName = name,
                requiredFieldErrors = it.requiredFieldErrors - AddMolRequiredField.FIRST_NAME
            )
        }
    }

    fun updateLastName(name: String) {
        updateState {
            it.copy(
                lastName = name,
                requiredFieldErrors = it.requiredFieldErrors - AddMolRequiredField.LAST_NAME
            )
        }
    }

    fun updatePhoneNumber(phone: String) {
        val filteredPhone = phone.filter { it.isDigit() }
        updateState {
            it.copy(
                phoneNumber = filteredPhone,
                requiredFieldErrors = it.requiredFieldErrors - AddMolRequiredField.PHONE_NUMBER
            )
        }
    }

    fun showCountryPicker() {
        updateState { it.copy(showCountryPicker = true) }
    }

    fun hideCountryPicker() {
        updateState { it.copy(showCountryPicker = false) }
    }

    fun selectCountry(countryId: Int, countryName: String, dialCode: String) {
        updateState {
            it.copy(
                dialCode = dialCode,
                showCountryPicker = false,
                requiredFieldErrors = it.requiredFieldErrors - AddMolRequiredField.DIAL_CODE
            )
        }
    }

    override fun onBackButtonClicked() {
        // When Add MOL is auto-opened from Step2 with empty signatories, skip the temporary
        // Step2 screen and return directly to the previous real screen (Step1B).
        val shouldSkipEmptyMolStep =
            config.editingIndex == null && sharedConfig.signatories.isEmpty()

        if (shouldSkipEmptyMolStep) {
            setResult<ParentCallback> {
                goBack { firstPopSucceeded ->
                    if (firstPopSucceeded) {
                        goBack()
                    }
                }
            }
            return
        }

        super.onBackButtonClicked()
    }

    fun onAdd() {
        val state = uiState.value
        val missing = mutableSetOf<AddMolRequiredField>()
        if (state.firstName.isBlank()) missing += AddMolRequiredField.FIRST_NAME
        if (state.lastName.isBlank()) missing += AddMolRequiredField.LAST_NAME
        if (state.dialCode.isBlank()) missing += AddMolRequiredField.DIAL_CODE
        if (state.phoneNumber.isBlank()) missing += AddMolRequiredField.PHONE_NUMBER
        if (missing.isNotEmpty()) {
            updateState {
                it.copy(
                    requiredFieldErrors = missing,
                    validationRequestId = it.validationRequestId + 1
                )
            }
            showSnackbar(sharedString(SR.strings.please_enter_all_required_fields))
            return
        }
        val newSignatory = SignatoryItem(
            firstName = state.firstName,
            lastName = state.lastName,
            phoneNumber = state.phoneNumber,
            dialCode = state.dialCode
        )
        val updatedSignatories = config.editingIndex?.let { index ->
            sharedConfig.signatories.toMutableList().apply {
                if (index in indices) set(index, newSignatory)
                else add(newSignatory)
            }
        } ?: (sharedConfig.signatories + newSignatory)
        sharedConfig.signatories = updatedSignatories
        pop()
    }
}
